import type { CSSProperties } from 'react';
import { cn } from '@sib/ui';

export const sanitizeColumnIdForCSSVariable = (id: string) =>
  id.replace(/[^a-zA-Z0-9_-]/g, '_');

export const getTableColumnSizeVariable = (column: string) =>
  `--col-${sanitizeColumnIdForCSSVariable(column)}-size`;
export const getTableHeaderSizeVariable = (header: string) =>
  `--header-${sanitizeColumnIdForCSSVariable(header)}-size`;

export const getTableColumnWidth = (column: string) =>
  `calc(var(${getTableColumnSizeVariable(column)}) * 1px)`;
export const getTableHeaderWidth = (header: string) =>
  `calc(var(${getTableHeaderSizeVariable(header)}) * 1px)`;

export const DATA_TABLE_HEADER_WIDTH_CLASS =
  'w-[calc(var(--dt-header-width)*1px)]';
export const DATA_TABLE_HEADER_MAX_WIDTH_CLASS =
  'max-w-[calc(var(--dt-header-width)*1px)]';
export const DATA_TABLE_PIN_LEFT_CLASS = 'left-[calc(var(--dt-pin-left)*1px)]';
export const DATA_TABLE_PIN_RIGHT_CLASS =
  'right-[calc(var(--dt-pin-right)*1px)]';

type DataTableHeaderCssVars = CSSProperties;

export const getDataTableHeaderWidthCssVars = (
  headerId: string
): DataTableHeaderCssVars =>
  ({
    '--dt-header-width': `var(${getTableHeaderSizeVariable(headerId)})`,
  }) as DataTableHeaderCssVars;

export const getDataTableHeaderPinningCssVars = (
  columnId: string,
  pinPosition: ColumnPinPosition
): DataTableHeaderCssVars => {
  if (pinPosition === 'left')
    return {
      '--dt-pin-left': `var(${getTableColumnPinLeftVariable(columnId)})`,
    } as DataTableHeaderCssVars;

  if (pinPosition === 'right')
    return {
      '--dt-pin-right': `var(${getTableColumnPinRightVariable(columnId)})`,
    } as DataTableHeaderCssVars;

  return {};
};

export const getDataTableHeaderCellCssVars = (
  headerId: string,
  columnId: string,
  pinPosition: ColumnPinPosition
): DataTableHeaderCssVars => ({
  ...getDataTableHeaderWidthCssVars(headerId),
  ...getDataTableHeaderPinningCssVars(columnId, pinPosition),
});

export const getTableHeaderWidthClassName = (withFixedColumnWidth: boolean) =>
  withFixedColumnWidth
    ? cn(
        DATA_TABLE_HEADER_WIDTH_CLASS,
        DATA_TABLE_HEADER_MAX_WIDTH_CLASS,
        'min-w-0'
      )
    : cn('min-w-0', DATA_TABLE_HEADER_WIDTH_CLASS);

export const getTableColumnPinLeftVariable = (column: string) =>
  `--col-${sanitizeColumnIdForCSSVariable(column)}-pin-left`;
export const getTableColumnPinRightVariable = (column: string) =>
  `--col-${sanitizeColumnIdForCSSVariable(column)}-pin-right`;

export const getTableColumnPinLeft = (column: string) =>
  `calc(var(${getTableColumnPinLeftVariable(column)}) * 1px)`;
export const getTableColumnPinRight = (column: string) =>
  `calc(var(${getTableColumnPinRightVariable(column)}) * 1px)`;

type ColumnPinPosition = false | 'left' | 'right';

export const getTableColumnPinningClassName = (
  pinPosition: ColumnPinPosition
) => {
  if (pinPosition === 'left') return DATA_TABLE_PIN_LEFT_CLASS;

  if (pinPosition === 'right') return DATA_TABLE_PIN_RIGHT_CLASS;

  return null;
};

type TableColumnFixedSizingStyle = Pick<
  CSSProperties,
  'width' | 'maxWidth' | 'minWidth'
>;

export const getTableColumnFixedSizingStyle = (
  columnId: string
): TableColumnFixedSizingStyle => {
  const width = getTableColumnWidth(columnId);

  return {
    width,
    maxWidth: width,
    minWidth: 0,
  };
};

export const getTableHeaderFixedSizingStyle = (
  headerId: string
): TableColumnFixedSizingStyle => {
  const width = getTableHeaderWidth(headerId);

  return {
    width,
    maxWidth: width,
    minWidth: 0,
  };
};

export const getTableGroupHeaderFixedSizingStyle = (
  headerSize: number
): Pick<CSSProperties, 'flex' | 'minWidth'> => ({
  flex: `${headerSize} 1 0`,
  minWidth: 0,
});

export const getTableColumnBorderVariable = (column: string) =>
  `--col-${sanitizeColumnIdForCSSVariable(column)}-color`;
